/* eslint-disable no-console */
/**
 * Service-worker-side client for the RAILGUN offscreen document.
 *
 * Phase 2 of RAILGUN_HYBRID_REWRITE_PLAN.md. The SW addresses the engine
 * exclusively through this module: ensure/teardown, wallet CRUD, balance
 * fetch, merkleRoot check, POI refresh. The offscreen owns the engine state.
 *
 * Why ports, not `chrome.runtime.sendMessage`:
 * The extension already has catch-all async `chrome.runtime.onMessage`
 * listeners in tab pages and `background.ts` that resolve to `null`/
 * `undefined` for unrecognized messages. Because Chrome resolves
 * `sendMessage` with the FIRST listener that returns a value (sync or via
 * Promise), our offscreen's slower async response was being beaten by those
 * `null`-returning listeners — every RPC except the very first looked like
 * "offscreen returned no response." `chrome.runtime.connect` ports route
 * only to the matching `onConnect` handler, so we get a private channel.
 *
 * Long-running operations (engine init, balance fetch) ack immediately and
 * push the real result back via the same port, keyed by `requestId`. This
 * also avoids the SW termination problem when the SW goes idle while
 * waiting for a slow response.
 *
 * Engine events (balanceUpdate, scan progress, batchListUpdate) push from
 * offscreen to SW over the port and we re-emit them via the same
 * `railgunEmitter` that controllers/UI subscribe to.
 *
 * Chrome-only. Callers must check `isRailgunOffscreenSupported` before using.
 */
import {
  RAILGUN_OFFSCREEN_PORT_NAME,
  RailgunOffscreenInitParams,
  RailgunOffscreenPushEvent,
  RailgunOffscreenRequest,
  RailgunOffscreenResponse,
  SerializedBalanceResult,
  SerializedChainSession,
  SerializedMerkleRootResult,
  SerializedNullifierStatusResult,
  SerializedTokenBalanceMap,
  SerializedWalletInfo
} from '@web/extension-services/offscreen/messages'
import { TXIDVersion } from '@railgun-community/shared-models'
import type { NetworkName } from '@railgun-community/shared-models'
import type { RailgunTokenBalance, RailgunTokenBalanceMap } from '@web/utils/railgunTokens'
import {
  emitRailgunEvent,
  type RailgunEventName,
  type RailgunListenerEvents
} from '@web/utils/railgunListener'
import { decodeBigInts, encodeBigInts } from '@web/utils/railgunSdkSerialize'
import eventBus from '@web/extension-services/event/eventBus'
import { getUiBackgroundDispatch } from './uiBackgroundDispatch'

const OFFSCREEN_DOCUMENT_PATH = 'offscreen.html'

const log = (...args: unknown[]) => console.log('[RailgunOffscreenClient]', ...args)

declare const chrome: {
  offscreen?: {
    createDocument: (opts: {
      url: string
      reasons: string[]
      justification: string
    }) => Promise<void>
    closeDocument: () => Promise<void>
    hasDocument?: () => Promise<boolean>
  }
  runtime: {
    getURL: (path: string) => string
    connect: (opts: { name: string }) => RailgunPort
    getContexts?: (filter: { contextTypes: string[] }) => Promise<{ contextType: string }[]>
  }
}

type RailgunPort = {
  postMessage: (msg: unknown) => void
  disconnect: () => void
  onMessage: { addListener: (cb: (msg: unknown) => void) => void }
  onDisconnect: { addListener: (cb: () => void) => void }
}

type PortRpcMessage =
  | { type: 'RPC'; requestId: string; request: RailgunOffscreenRequest }
  | { type: 'RPC_RESULT'; requestId: string; response: RailgunOffscreenResponse }
  | { type: 'PUSH'; push: RailgunOffscreenPushEvent }

let createPromise: Promise<void> | null = null
let port: RailgunPort | null = null
let portConnectPromise: Promise<RailgunPort> | null = null

type PendingRpc = {
  resolve: (response: RailgunOffscreenResponse) => void
  reject: (err: Error) => void
}
const pendingRpcs: Map<string, PendingRpc> = new Map()

type ProgressListener = (progress: number) => void
const progressListeners: Map<string, ProgressListener> = new Map()

type BalanceResultResolver = {
  resolve: (result: BalanceResult) => void
  reject: (err: Error) => void
}
const pendingBalanceRequests: Map<string, BalanceResultResolver> = new Map()

export type BalanceResult = {
  balance: RailgunTokenBalanceMap
  pendingBalance: RailgunTokenBalanceMap
  missingInternalPOIBalance: RailgunTokenBalanceMap
  missingExternalPOIBalance: RailgunTokenBalanceMap
}

function deserializeBalanceMap(serialized: SerializedTokenBalanceMap): RailgunTokenBalanceMap {
  const map = new Map<number, RailgunTokenBalance[]>()
  for (const [chainId, balances] of serialized) {
    // Amounts cross the wire as decimal strings to keep the JSON portable,
    // and stay strings at rest — matches what the SDK actually returns at
    // runtime today (the local `amount: bigint` type is a fiction; downstream
    // code coerces via `Number(amount)` / `BigInt(amount)` at every use site).
    map.set(
      chainId,
      balances.map((b) => ({
        tokenAddress: b.tokenAddress,
        amount: b.amount as unknown as bigint
      })) as RailgunTokenBalance[]
    )
  }
  return map
}

function deserializeBalanceResult(serialized: SerializedBalanceResult): BalanceResult {
  return {
    balance: deserializeBalanceMap(serialized.balance),
    pendingBalance: deserializeBalanceMap(serialized.pendingBalance),
    missingInternalPOIBalance: deserializeBalanceMap(serialized.missingInternalPOIBalance),
    missingExternalPOIBalance: deserializeBalanceMap(serialized.missingExternalPOIBalance)
  }
}

function rejectAllPending(reason: string): void {
  for (const [, pending] of pendingRpcs) pending.reject(new Error(reason))
  pendingRpcs.clear()
  for (const [, pending] of pendingBalanceRequests) pending.reject(new Error(reason))
  pendingBalanceRequests.clear()
}

function handlePortMessage(msg: unknown): void {
  const m = msg as PortRpcMessage
  if (!m || typeof m !== 'object') return
  if (m.type === 'RPC_RESULT') {
    const pending = pendingRpcs.get(m.requestId)
    if (!pending) return
    pendingRpcs.delete(m.requestId)
    pending.resolve(m.response)
    return
  }
  if (m.type === 'PUSH') {
    const push = m.push
    if (push.type === 'OFFSCREEN_EVENT') {
      const eventName = push.event as RailgunEventName
      const payload = push.payload as RailgunListenerEvents[typeof eventName]
      try {
        emitRailgunEvent(eventName, payload)
      } catch (err) {
        log('event re-emit failed', eventName, (err as Error)?.message || err)
      }
      return
    }
    if (push.type === 'OFFSCREEN_PROGRESS') {
      const cb = progressListeners.get(push.progressId)
      if (cb) {
        try {
          cb(push.progress)
        } catch {
          /* ignore */
        }
      }
      return
    }
    if (push.type === 'OFFSCREEN_BALANCE_RESULT') {
      const pending = pendingBalanceRequests.get(push.requestId)
      if (!pending) return
      pendingBalanceRequests.delete(push.requestId)
      if (push.ok) {
        pending.resolve(deserializeBalanceResult(push.result))
      } else {
        pending.reject(new Error(push.error))
      }
    }
  }
}

async function hasOffscreenDocument(): Promise<boolean> {
  // Chrome 116+ exposes `getContexts` which is the canonical way to detect an
  // existing offscreen document. Fall back to the legacy `hasDocument` shim if
  // present, otherwise assume none and let `createDocument` throw on duplicate.
  const runtime = chrome.runtime as { getContexts?: Function }
  if (typeof runtime.getContexts === 'function') {
    try {
      const contexts = await chrome.runtime.getContexts!({ contextTypes: ['OFFSCREEN_DOCUMENT'] })
      return contexts.length > 0
    } catch {
      // fall through
    }
  }
  if (chrome.offscreen?.hasDocument) {
    try {
      return await chrome.offscreen.hasDocument()
    } catch {
      return false
    }
  }
  return false
}

export async function ensureRailgunOffscreen(): Promise<void> {
  if (!isRailgunOffscreenHostAvailable) {
    throw new Error('Railgun offscreen host not available on this target')
  }
  if (!chrome.offscreen) {
    throw new Error('chrome.offscreen unavailable — offscreen documents require MV3 + Chrome 109+')
  }
  if (await hasOffscreenDocument()) return
  if (createPromise) {
    await createPromise
    return
  }
  createPromise = (async () => {
    log('creating offscreen document')
    try {
      await chrome.offscreen!.createDocument({
        url: chrome.runtime.getURL(OFFSCREEN_DOCUMENT_PATH),
        reasons: ['WORKERS'],
        justification:
          'Hosts the RAILGUN cryptographic indexer + SnarkJS prover. The service worker cannot hold these (no URL.createObjectURL, killed every 30s).'
      })
    } catch (err: any) {
      if (typeof err?.message === 'string' && /single offscreen document/i.test(err.message)) {
        log('offscreen already exists (race), continuing')
      } else {
        throw err
      }
    }
  })()
  try {
    await createPromise
  } finally {
    createPromise = null
  }
}

async function ensurePort(): Promise<RailgunPort> {
  if (!isRailgunOffscreenHostAvailable) {
    throw new Error('Railgun offscreen host not available on this target')
  }
  // Validate any cached port against the offscreen document's actual presence in
  // UI context. Chrome doesn't always fire `onDisconnect` synchronously when the
  // offscreen page is torn down (especially when the SW closes it via
  // `closeDocument` while no message is in flight). Without this validation, a
  // stale port reference looks "live" — `postMessage` silently no-ops and the
  // pending rpc waits up to 5 minutes for a response that never comes. SW
  // doesn't need this check (it owns the lifecycle), but UI does.
  if (port && isUiContext) {
    try {
      if (!(await hasOffscreenDocument())) {
        log('ensurePort: offscreen gone, dropping stale port')
        try { port.disconnect() } catch { /* ignore */ }
        port = null
        rejectAllPending('offscreen torn down (stale port)')
      }
    } catch {
      /* getContexts unavailable — fall through */
    }
  }
  if (port) return port
  if (portConnectPromise) return portConnectPromise
  portConnectPromise = (async () => {
    // Only the SW can call `chrome.offscreen.createDocument`. In Chrome 130+
    // the namespace itself is exposed to extension pages, so the previous
    // `if (chrome.offscreen)` check passed from tabs and threw "Only the
    // service worker can call createDocument" — which didn't match the
    // disconnect-error pattern that triggers the recovery wrapper, so the
    // UI saw an unrecoverable error instead of self-healing.
    const isServiceWorker = typeof (globalThis as any).importScripts === 'function'
    if (isServiceWorker) {
      await ensureRailgunOffscreen()
    } else {
      // From a tab, `chrome.runtime.connect` to a non-existent offscreen does
      // NOT fire `onDisconnect` — it silently returns a live-looking port,
      // and the eventual `postMessage` is dropped on the floor. The rpc
      // would then sit waiting until the 30s default timeout (or 5-min
      // SDK_INVOKE timeout) — neither of which matches the
      // disconnect-error pattern that triggers recovery. So in UI context
      // we proactively gate `connect` on two conditions: the offscreen
      // document exists AND the SW has reported `engineInitialized=true`.
      // The second check matters because an idle-stop tear-down or an
      // SW-initiated background sync can transiently re-create the
      // offscreen before the engine + wallet have finished re-loading;
      // sending an SDK_INVOKE during that window hangs until the timeout.
      if (!(await hasOffscreenDocument()) || !lastSeenEngineInitialized) {
        await triggerEngineRecovery()
      }
    }
    const p = chrome.runtime.connect({ name: RAILGUN_OFFSCREEN_PORT_NAME })
    p.onMessage.addListener(handlePortMessage)
    p.onDisconnect.addListener(() => {
      log('port disconnected')
      port = null
      rejectAllPending('offscreen port disconnected')
    })
    port = p
    return p
  })()
  try {
    return await portConnectPromise
  } finally {
    portConnectPromise = null
  }
}

// Per-call timeout. Long enough that a healthy interactive RPC (status, list,
// hibernate/activate, gas estimate) is never tripped, short enough that a hung
// port doesn't wedge a UI poll loop forever. Long-running RPCs override this:
//   - WALLET_GET_BALANCE: ack returns BALANCE_PENDING immediately; the real
//     result arrives via a separate push channel — 30s default is fine.
//   - SDK_INVOKE for proof generation (generateUnshieldProof,
//     generatePrivateTransferProof) routinely takes 20-90s synchronously on
//     consumer hardware. Caller passes a longer timeout via sdkInvokeInOffscreen.
const RPC_DEFAULT_TIMEOUT_MS = 30_000
// Generous bound for synchronous proof generation. Bigger than any realistic
// snark prove time we've measured; still finite so a genuine hang doesn't
// wedge a spend forever.
const SDK_INVOKE_TIMEOUT_MS = 5 * 60_000
// STATUS RPC is the heartbeat used by the UI's `ensureRailgunEngine` poll loop.
// During first-boot the SW is busy with `loadWalletsIntoMemory` + per-network
// `merkleRoot` checks for ~20-30s, during which the message handler may not
// drain promptly. A 30s per-call timeout used to flake here and wedge the
// Private toggle on error. STATUS is cheap and idempotent — give it a much
// wider window so transient SW busy-ness doesn't propagate as init failure.
const STATUS_RPC_TIMEOUT_MS = 90_000

// UI-context recovery state. SW recreates the offscreen document inline
// via its own `ensureRailgunOffscreen` path; only UI tabs/popups need to
// dispatch through the messenger and wait for the controller-state flip.
//
// Detection note: we detect SW context by the presence of ServiceWorker
// globals (`importScripts` is one), NOT by checking `chrome.offscreen`.
// In Chrome 130+ the `chrome.offscreen` namespace and its methods are
// exposed to extension pages as well — they fail at call time with
// "Only the service worker can call createDocument", but the truthy check
// passes from a tab. Using that as a SW marker silently bypassed the
// recovery wrapper from UI tabs (port disconnects rejected with no retry).
const isUiContext =
  typeof window !== 'undefined' && typeof (globalThis as any).importScripts !== 'function'

// Mobile-as-webapp target (Webpack EXPO_OS=web, no MV3 host): chrome runtime
// absent. The web Railgun context still imports this client; without an early
// guard the recovery loop fires every action and spams logs. All public
// entry points bail with a clean error mapped to `RailgunEngineUnavailableError`.
export const isRailgunOffscreenHostAvailable =
  typeof chrome !== 'undefined' && !!(chrome as any)?.runtime?.id

export class RailgunEngineUnavailableError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'RailgunEngineUnavailableError'
  }
}

// Mirror of `RailgunBackgroundController.engineInitialized`, kept fresh by an
// eventBus listener installed at module load. Used by recovery to know when
// the SW has finished re-warming the engine without polling controller state
// over the messenger every 200ms.
let lastSeenEngineInitialized = false
if (isUiContext) {
  try {
    eventBus.addEventListener('RailgunBackgroundController', (newState: any) => {
      lastSeenEngineInitialized = !!newState?.engineInitialized
    })
  } catch {
    /* eventBus unavailable — recovery falls back to dispatch-and-wait */
  }
}

// Singleton recovery promise: when N concurrent RPCs all reject with
// "offscreen port disconnected" (rejectAllPending fires once), they all
// arrive at recovery in the same tick. Without de-dupe each would dispatch
// `ENSURE_OFFSCREEN_ENGINE` and poll independently — the SW would receive N
// ensure requests and we'd burn the recovery budget N times. The promise is
// cleared in finally so a *later* failure (after this recovery is done) can
// trigger a fresh attempt.
let recoveryPromise: Promise<void> | null = null
const RECOVERY_TIMEOUT_MS = 15_000
const RECOVERY_POLL_MS = 200

async function triggerEngineRecovery(): Promise<void> {
  if (!isRailgunOffscreenHostAvailable) {
    throw new RailgunEngineUnavailableError(
      'Railgun offscreen host not available on this target'
    )
  }
  if (recoveryPromise) return recoveryPromise
  recoveryPromise = (async () => {
    log('recover: attempting')
    eventBus.emit('railgunEngineRecoveryStatus', { status: 'recovering' })
    const dispatch = getUiBackgroundDispatch()
    if (!dispatch) {
      throw new Error('UI background dispatch not registered')
    }
    // We just observed the offscreen die. Treat the engine as down until the
    // SW broadcasts engineInitialized=true via controller-state. Without this
    // reset, a stale `true` from before idle-stop would short-circuit the
    // wait and we'd retry against a still-dead port.
    lastSeenEngineInitialized = false
    dispatch({ type: 'RAILGUN_BACKGROUND_CONTROLLER_ENSURE_OFFSCREEN_ENGINE' })
    const startedAt = Date.now()
    // Wait until BOTH the controller signals ready AND the offscreen
    // document actually exists. The two can drift: the controller's
    // `engineInitialized` flag is bookkeeping that can be stale if the
    // offscreen was torn down outside the controller's own lifecycle
    // (e.g. browser-internal eviction, or the controller short-circuiting
    // `#ensureEngine` because it still thinks the engine is up). Polling
    // both closes the gap.
    // eslint-disable-next-line no-constant-condition
    while (true) {
      // eslint-disable-next-line no-await-in-loop
      const offscreenAlive = await hasOffscreenDocument()
      if (lastSeenEngineInitialized && offscreenAlive) break
      if (Date.now() - startedAt > RECOVERY_TIMEOUT_MS) {
        throw new Error(
          `engine recovery timed out after ${RECOVERY_TIMEOUT_MS}ms ` +
            `(engineInit=${lastSeenEngineInitialized} offscreenAlive=${offscreenAlive})`
        )
      }
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, RECOVERY_POLL_MS))
    }
    log('recover: ok')
  })()
  try {
    await recoveryPromise
    eventBus.emit('railgunEngineRecoveryStatus', { status: 'ok' })
  } catch (err) {
    log('recover: failed', (err as Error)?.message || err)
    eventBus.emit('railgunEngineRecoveryStatus', {
      status: 'failed',
      reason: (err as Error)?.message || 'unknown'
    })
    throw err
  } finally {
    recoveryPromise = null
  }
}

/**
 * UI hook surface: imperatively kick a recovery attempt. Used by the
 * recovery banner's Retry button after a failed silent recover.
 * Resolves when the engine is ready again, rejects on persistent failure.
 */
export async function retryEngineRecoveryFromUi(): Promise<void> {
  if (!isUiContext) {
    throw new Error('retryEngineRecoveryFromUi is UI-only')
  }
  await triggerEngineRecovery()
}

function isPortDisconnectError(err: unknown): boolean {
  const m = (err as { message?: string } | undefined)?.message
  if (typeof m !== 'string') return false
  return /offscreen port disconnected|offscreen closed/i.test(m)
}

async function rpc(
  request: RailgunOffscreenRequest,
  timeoutMs: number = RPC_DEFAULT_TIMEOUT_MS
): Promise<RailgunOffscreenResponse> {
  try {
    return await rpcRaw(request, timeoutMs)
  } catch (err) {
    // Self-heal only in UI contexts. SW callers manage offscreen lifecycle
    // themselves and must surface failures unmodified. Recovery covers the
    // disconnect case (idle-stop tore down offscreen, port died); other
    // errors (bad params, SDK throw) bubble up unchanged.
    if (!isUiContext) throw err
    if (!isPortDisconnectError(err)) throw err
    try {
      await triggerEngineRecovery()
    } catch (recoveryErr) {
      throw new RailgunEngineUnavailableError(
        `Railgun engine unavailable: ${(recoveryErr as Error)?.message || 'unknown'}`
      )
    }
    // One retry only. If the freshly-warmed engine still rejects with a
    // disconnect, surface a typed error so callers can show a banner instead
    // of looping into another recovery cycle.
    try {
      return await rpcRaw(request, timeoutMs)
    } catch (retryErr) {
      if (isPortDisconnectError(retryErr)) {
        throw new RailgunEngineUnavailableError(
          `Railgun engine unavailable after recovery: ${(retryErr as Error).message}`
        )
      }
      throw retryErr
    }
  }
}

async function rpcRaw(
  request: RailgunOffscreenRequest,
  timeoutMs: number
): Promise<RailgunOffscreenResponse> {
  const p = await ensurePort()
  const requestId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
  return new Promise<RailgunOffscreenResponse>((resolve, reject) => {
    // Per-call timeout: if the offscreen port hangs (e.g. SW killed offscreen
    // mid-call, port is in a disconnecting limbo, or a handler silently never
    // posts a response), clean up and reject so callers can retry instead of
    // awaiting forever. Without this, a single hung await wedges UI poll loops
    // (the outer 5-min timeout in `ensureRailgunEngine` only fires between
    // iterations, not during a single `await rpc(...)`).
    const timer = setTimeout(() => {
      if (pendingRpcs.delete(requestId)) {
        reject(new Error(`offscreen rpc timed out after ${timeoutMs}ms (${request.type})`))
      }
    }, timeoutMs)
    const wrap = {
      resolve: (r: RailgunOffscreenResponse) => {
        clearTimeout(timer)
        resolve(r)
      },
      reject: (e: Error) => {
        clearTimeout(timer)
        reject(e)
      }
    }
    pendingRpcs.set(requestId, wrap)
    try {
      p.postMessage({ type: 'RPC', requestId, request } as PortRpcMessage)
    } catch (err) {
      pendingRpcs.delete(requestId)
      clearTimeout(timer)
      reject(err as Error)
    }
  })
}

export async function closeRailgunOffscreen(): Promise<void> {
  if (!chrome.offscreen) return
  if (port) {
    try {
      port.disconnect()
    } catch {
      /* ignore */
    }
    port = null
    rejectAllPending('offscreen closed')
  }
  if (!(await hasOffscreenDocument())) return
  try {
    await chrome.offscreen.closeDocument()
    log('offscreen closed')
  } catch (err) {
    log('closeDocument failed', err)
  }
}

export async function pingRailgunOffscreen(): Promise<boolean> {
  const res = await rpc({ type: 'PING' })
  return res.ok && res.type === 'PONG'
}

export async function initRailgunEngineInOffscreen(
  params: RailgunOffscreenInitParams
): Promise<void> {
  // Offscreen acks INIT_ENGINE immediately and runs SDK init in the
  // background; we poll STATUS so the response is still meaningful.
  const ack = await rpc({ type: 'INIT_ENGINE', params })
  if (!ack.ok) throw new Error(`offscreen INIT_ENGINE rejected: ${ack.message}`)
  const startedAt = Date.now()
  const TIMEOUT_MS = 5 * 60 * 1000
  const POLL_MS = 1000
  // eslint-disable-next-line no-constant-condition
  while (true) {
    // eslint-disable-next-line no-await-in-loop
    const status = await rpc({ type: 'STATUS' }, STATUS_RPC_TIMEOUT_MS)
    if (status.ok && status.type === 'STATUS' && status.engineInitialized) return
    if (Date.now() - startedAt > TIMEOUT_MS) {
      throw new Error('offscreen engine init timed out after 5 min')
    }
    // eslint-disable-next-line no-await-in-loop
    await new Promise((r) => setTimeout(r, POLL_MS))
  }
}

export async function stopRailgunEngineInOffscreen(): Promise<void> {
  if (!(await hasOffscreenDocument())) return
  const res = await rpc({ type: 'STOP_ENGINE' })
  if (!res.ok) throw new Error(`offscreen STOP_ENGINE failed: ${res.message}`)
}

export async function getRailgunOffscreenStatus(): Promise<{
  engineInitialized: boolean
  bootedAt: number | null
} | null> {
  if (!(await hasOffscreenDocument())) return null
  const res = await rpc({ type: 'STATUS' }, STATUS_RPC_TIMEOUT_MS)
  if (!res.ok || res.type !== 'STATUS') return null
  return { engineInitialized: res.engineInitialized, bootedAt: res.bootedAt }
}

export async function createWalletFromSeedInOffscreen(params: {
  mnemonic: string
  accountIndex: number
  passphrase?: string
}): Promise<SerializedWalletInfo> {
  const res = await rpc({
    type: 'WALLET_CREATE_FROM_SEED',
    mnemonic: params.mnemonic,
    accountIndex: params.accountIndex,
    passphrase: params.passphrase
  })
  if (!res.ok || res.type !== 'WALLET_INFO') {
    throw new Error(
      `offscreen WALLET_CREATE_FROM_SEED failed: ${res.ok ? 'unexpected response' : res.message}`
    )
  }
  return res.wallet
}

// In-flight de-dupe: the UI's `handleLoadRailgunWallets` effect can re-fire while
// a previous load is still pending (accounts/keys deps churn during a send flow).
// Without this, a second call queues behind the first on the offscreen and trips
// the rpc timeout before the original returns.
const inFlightWalletLoads = new Map<string, Promise<SerializedWalletInfo>>()

/**
 * True when `loadWalletByIdInOffscreen` failed because the wallet's primary
 * encrypted record is absent from the engine DB while per-chain sub-records
 * (`wallet:<id>:<chain>:details`, etc.) may still exist — i.e. partial-state
 * corruption from an aborted create or a previous storage wipe that left the
 * `chrome.storage.local.railgunWallets` row pointing at a now-orphaned id.
 *
 * Callers (SW warmup, UI load) recover by deleting the stale row and calling
 * `createWalletFromSeedInOffscreen`. Because both `walletId` and
 * `encryptionKey` are deterministic from `(mnemonic, accountIndex)`, the
 * recreated wallet matches the orphaned chain sub-records, so balance scan
 * resumes from prior progress instead of restarting from genesis.
 *
 * Match string is the cause chain surfaced by the offscreen handler — see
 * `extension-services/offscreen/index.ts` `WALLET_LOAD_BY_ID` catch.
 */
export function isWalletPrimaryRecordMissingError(err: unknown): boolean {
  const message = (err as { message?: string } | undefined)?.message
  if (typeof message !== 'string') return false
  return message.includes('Key not found in database')
}

export async function loadWalletByIdInOffscreen(params: {
  walletId: string
  encryptionKey: string
}): Promise<SerializedWalletInfo> {
  const existing = inFlightWalletLoads.get(params.walletId)
  if (existing) return existing
  const promise = (async () => {
    // Use the long SDK_INVOKE timeout: WALLET_LOAD_BY_ID can be slow when the
    // offscreen event loop is busy with proof generation or a balance scan.
    // Idempotent on the offscreen side (cached short-circuit), so a wider
    // window is safe.
    const res = await rpc(
      {
        type: 'WALLET_LOAD_BY_ID',
        walletId: params.walletId,
        encryptionKey: params.encryptionKey
      },
      SDK_INVOKE_TIMEOUT_MS
    )
    if (!res.ok || res.type !== 'WALLET_INFO') {
      throw new Error(
        `offscreen WALLET_LOAD_BY_ID failed: ${res.ok ? 'unexpected response' : res.message}`
      )
    }
    return res.wallet
  })()
  inFlightWalletLoads.set(params.walletId, promise)
  try {
    return await promise
  } finally {
    inFlightWalletLoads.delete(params.walletId)
  }
}

export async function getBalanceInOffscreen(params: {
  walletId: string
  networkNames: NetworkName[]
}): Promise<BalanceResult> {
  const requestId = `bal-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
  const result = new Promise<BalanceResult>((resolve, reject) => {
    pendingBalanceRequests.set(requestId, { resolve, reject })
  })
  const ack = await rpc({
    type: 'WALLET_GET_BALANCE',
    walletId: params.walletId,
    networkNames: params.networkNames as unknown as string[],
    requestId
  })
  if (!ack.ok) {
    pendingBalanceRequests.delete(requestId)
    throw new Error(`offscreen WALLET_GET_BALANCE rejected: ${ack.message}`)
  }
  return result
}

export async function fetchTokenBalanceInOffscreen(params: {
  walletId: string
  networkName: NetworkName
  chainId: number
  tokenAddress: string
}): Promise<string> {
  // Internally calls wallet.getBalance which can do a full chain scan on a
  // cold cache. Use the longer SDK_INVOKE timeout rather than the 30s default.
  const res = await rpc(
    {
      type: 'WALLET_FETCH_TOKEN_BALANCE',
      walletId: params.walletId,
      networkName: params.networkName,
      chainId: params.chainId,
      tokenAddress: params.tokenAddress
    },
    SDK_INVOKE_TIMEOUT_MS
  )
  if (!res.ok || res.type !== 'TOKEN_BALANCE') {
    throw new Error(
      `offscreen WALLET_FETCH_TOKEN_BALANCE failed: ${res.ok ? 'unexpected' : res.message}`
    )
  }
  return res.amount
}

export async function verifyMerkleRootsInOffscreen(
  networkNames: NetworkName[]
): Promise<SerializedMerkleRootResult[]> {
  const res = await rpc({
    type: 'VERIFY_MERKLE_ROOTS',
    networkNames: networkNames as unknown as string[]
  })
  if (!res.ok || res.type !== 'MERKLE_ROOTS') {
    throw new Error(
      `offscreen VERIFY_MERKLE_ROOTS failed: ${res.ok ? 'unexpected' : res.message}`
    )
  }
  return res.results
}

/**
 * Phase 4.2 — pre-spend nullifier verification via the offscreen quorum.
 * Caller passes the nullifiers it's about to spend; result `presence[n] === true`
 * means that nullifier was found on-chain in the scanned window — the local
 * cache is stale and the spend MUST be refused.
 */
export async function verifyNullifiersInOffscreen(params: {
  chainId: number
  nullifiers: string[]
  fromBlock?: number
  toBlock?: number
  lookbackBlocks?: number
}): Promise<SerializedNullifierStatusResult> {
  const res = await rpc({
    type: 'VERIFY_NULLIFIERS',
    chainId: params.chainId,
    nullifiers: params.nullifiers,
    ...(params.fromBlock != null ? { fromBlock: params.fromBlock } : {}),
    ...(params.toBlock != null ? { toBlock: params.toBlock } : {}),
    ...(params.lookbackBlocks != null ? { lookbackBlocks: params.lookbackBlocks } : {})
  })
  if (!res.ok || res.type !== 'NULLIFIER_STATUS') {
    throw new Error(
      `offscreen VERIFY_NULLIFIERS failed: ${res.ok ? 'unexpected' : res.message}`
    )
  }
  return res.result
}

/**
 * Generic SDK call proxy. The offscreen's `sdkRegistry` resolves `method`
 * to a bound SDK function; args travel as bigint-safe encoded values.
 *
 * `onProgress` is invoked when the called method emits proof-progress
 * callbacks (i.e. the proving entry points). The progress channel is one
 * direction only; tearing down the listener happens automatically when the
 * RPC settles.
 */
export async function sdkInvokeInOffscreen<T = unknown>(params: {
  method: string
  args: unknown[]
  onProgress?: (progress: number) => void
}): Promise<T> {
  const { method, args, onProgress } = params
  let progressId: string | undefined
  if (onProgress) {
    progressId = `prog-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
    progressListeners.set(progressId, onProgress)
  }
  try {
    const res = await rpc(
      {
        type: 'SDK_INVOKE',
        method,
        argsEncoded: encodeBigInts(args),
        progressId
      },
      SDK_INVOKE_TIMEOUT_MS
    )
    if (!res.ok || res.type !== 'SDK_RESULT') {
      throw new Error(`offscreen SDK_INVOKE(${method}) failed: ${res.ok ? 'unexpected' : res.message}`)
    }
    return decodeBigInts(res.resultEncoded) as T
  } finally {
    if (progressId) progressListeners.delete(progressId)
  }
}

/**
 * Re-kick the engine UTXO scan for one chain. Calls the SDK `refreshBalances`, which runs
 * `engine.scanContractHistory(chain)` — resuming the scan from the last synced block. Used by
 * the background stall watchdog to revive a chain whose cold scan died in the engine's
 * getLogs-timeout retry path (30× 5s timeouts → throw). Uses the long SDK_INVOKE timeout
 * because the resumed scan can run for a while. Fire-and-(await) — caller guards cooldown.
 */
export async function reKickChainScanInOffscreen(chainId: number): Promise<void> {
  await sdkInvokeInOffscreen<void>({
    method: 'refreshBalances',
    // chain = { type: ChainType.EVM (0), id }, walletIdFilter = undefined (all wallets)
    args: [{ type: 0, id: chainId }, undefined]
  })
}

/**
 * P3 (railgun-spend-engine-warm-plan.md §5 step 5). Drop the main offscreen
 * engine's in-RAM UTXO merkletree cache for one chain so the next spend
 * estimate/proof re-reads the tree the per-chain view-only worker appended into
 * the shared DB. No-op unless `RAILGUN_SHARED_SCAN_DB` is on; cheap (RAM reset)
 * and idempotent. Best-effort: a failure here only means the cache stays warm,
 * which the estimate path tolerates (it falls back to a fresh read on cache
 * miss), so callers swallow errors rather than block the spend.
 */
export async function clearMerkletreeRamCacheInOffscreen(
  chainId: number,
  walletId?: string
): Promise<void> {
  await sdkInvokeInOffscreen<boolean>({
    method: '__clearMerkletreeRamCacheForChain',
    args: [TXIDVersion.V2_PoseidonMerkle, { type: 0, id: chainId }, walletId]
  })
}

export async function refreshReceivePOIsInOffscreen(params: {
  walletId: string
  chainId: number
}): Promise<void> {
  const res = await rpc({
    type: 'REFRESH_RECEIVE_POIS',
    walletId: params.walletId,
    chainId: params.chainId
  })
  if (!res.ok) {
    throw new Error(`offscreen REFRESH_RECEIVE_POIS failed: ${res.message}`)
  }
}

/**
 * Sender-side POI submission kick (offscreen). Generates + submits the wallet's
 * pending transact-proofs so a recipient's MissingExternalPOI clears. Resolves once
 * the SDK's generate cycle is kicked; poll `getPendingSpentPOIsInOffscreen` for completion.
 */
export async function generateSpentPOIsInOffscreen(params: {
  walletId: string
  chainId: number
}): Promise<void> {
  const res = await rpc({
    type: 'GENERATE_SPENT_POIS',
    walletId: params.walletId,
    chainId: params.chainId
  })
  if (!res.ok) {
    throw new Error(`offscreen GENERATE_SPENT_POIS failed: ${res.message}`)
  }
}

/**
 * Completion check for the sender-POI kick: on-chain txids whose spent-commitment
 * POIs are still un-submitted on this chain. Empty array = all submitted.
 */
export async function getPendingSpentPOIsInOffscreen(params: {
  walletId: string
  chainId: number
}): Promise<string[]> {
  const res = await rpc({
    type: 'GET_PENDING_SPENT_POIS',
    walletId: params.walletId,
    chainId: params.chainId
  })
  if (!res.ok) {
    throw new Error(`offscreen GET_PENDING_SPENT_POIS failed: ${res.message}`)
  }
  if (res.type !== 'PENDING_SPENT_POIS') {
    throw new Error(`offscreen GET_PENDING_SPENT_POIS unexpected response: ${res.type}`)
  }
  return res.railgunTxids
}

/**
 * Phase 3.1 — list per-chain session metadata. Returns one entry per
 * NETWORKS_FOR_BALANCE chain. Cheap (~1 chrome.storage read), safe to call
 * before the engine is up.
 */
export async function listChainSessionsInOffscreen(): Promise<SerializedChainSession[]> {
  const res = await rpc({ type: 'SESSION_LIST' })
  if (!res.ok || res.type !== 'SESSION_LIST') {
    throw new Error(`offscreen SESSION_LIST failed: ${res.ok ? 'unexpected response' : res.message}`)
  }
  return res.sessions
}

/**
 * Phase 3.1 — flip a session into `active`. Today this is metadata-only
 * (Phase 3.2 will add provider load + incremental sync + merkleRoot anchor).
 */
export async function activateChainSessionInOffscreen(
  chainId: number
): Promise<SerializedChainSession> {
  const res = await rpc({ type: 'SESSION_ACTIVATE', chainId })
  if (!res.ok || res.type !== 'SESSION_INFO') {
    throw new Error(
      `offscreen SESSION_ACTIVATE failed: ${res.ok ? 'unexpected response' : res.message}`
    )
  }
  return res.session
}

/**
 * Phase 3.1 — flip a session into `hibernated`. Today metadata-only;
 * Phase 3.2 may add `unloadProvider` for chains idle past a threshold.
 */
export async function hibernateChainSessionInOffscreen(
  chainId: number
): Promise<SerializedChainSession> {
  const res = await rpc({ type: 'SESSION_HIBERNATE', chainId })
  if (!res.ok || res.type !== 'SESSION_INFO') {
    throw new Error(
      `offscreen SESSION_HIBERNATE failed: ${res.ok ? 'unexpected response' : res.message}`
    )
  }
  return res.session
}

/**
 * Phase 3.3 — opportunistic re-anchor sweep across every registered chain.
 * Called by the SW chrome.alarms tick; never throws back to the alarm so a
 * transient offscreen failure can't kill the recurring schedule.
 */
export async function reanchorAllChainSessionsInOffscreen(): Promise<SerializedChainSession[]> {
  const res = await rpc({ type: 'SESSION_REANCHOR_ALL' })
  if (!res.ok || res.type !== 'SESSION_LIST') {
    throw new Error(
      `offscreen SESSION_REANCHOR_ALL failed: ${res.ok ? 'unexpected response' : res.message}`
    )
  }
  return res.sessions
}

/**
 * Mirrors the surface of `RailgunWallet` so SW callsites can hold either the
 * in-process wallet (flag off) or this remote handle (flag on) without
 * branching on every call. Methods proxy through the offscreen port.
 */
export class OffscreenRailgunWalletProxy {
  #info: SerializedWalletInfo

  constructor(info: SerializedWalletInfo) {
    this.#info = info
  }

  getWalletId(): string {
    return this.#info.walletId
  }

  getEncryptionKey(): string {
    return this.#info.encryptionKey
  }

  async getShareableViewingKey(): Promise<string> {
    return this.#info.viewingKey
  }

  async getRailgunAddress(): Promise<string> {
    return this.#info.railgunAddress
  }

  async getBalance(networkNames: NetworkName[]): Promise<BalanceResult> {
    return getBalanceInOffscreen({ walletId: this.#info.walletId, networkNames })
  }

  async getTransactionHistory<T = unknown>(
    networkName: NetworkName,
    startingBlock?: number
  ): Promise<T[]> {
    const sharedModels = (await import('@railgun-community/shared-models')) as unknown as {
      NETWORK_CONFIG: Record<string, { chain: unknown } | undefined>
    }
    const cfg = sharedModels.NETWORK_CONFIG[networkName]
    if (!cfg) throw new Error(`No NETWORK_CONFIG for ${networkName}`)
    return sdkInvokeInOffscreen<T[]>({
      method: 'getWalletTransactionHistory',
      args: [cfg.chain, this.#info.walletId, startingBlock ?? null]
    })
  }
}
